/**
 * {paymentNotice}の一覧 /payment-notices（#233 3b、docs/93 §15.6）。左のメニューには足さず、「仕入」の一覧の上の切り替え「{paymentNotice}」から。
 *   タブ：確認待ち／確認済／すべて（件数つき）。探す（{vendor}・番号・件名）。
 *   列：発行日・No.・{vendor}・{project}・件名・金額（税込）・状態（確認待ち n日／確認済 MM/DD／確認済（期限経過）／誤りあり／下書き）
 *   「{paymentNotice}作成」は PC だけ（/payment-notices/new。スマホは閲覧のみ）。{project}のタブ「仕入」にも同じ表（GroupNotices）
 */
import { NOTICE_TAB_LABEL, PAYMENT_NOTICE_MSG, type NoticeTab, type PaymentNoticeList, type PaymentNoticeSummary } from "@genbaryoku/contract";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, messageOf } from "../../api.js";
import { usePageName } from "../../components/BackButton.jsx";
import { FilterInput, ListHead } from "../../components/ListHead.jsx";
import { ListTabs, listTabPanel } from "../../components/ListTabs.jsx";
import { Icon } from "../../components/Shell.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { mdSlash, yen } from "../../lib/num.js";
import { usePcOnly } from "../../lib/pcOnly.js";
import { NoticeStateChip, PurchaseKindSwitch } from "./parts.jsx";

const TABS: NoticeTab[] = ["waiting", "confirmed", "all"];

export function PaymentNoticeListPage() {
  const [sp, setSp] = useSearchParams();
  const tab = (TABS.find((k) => k === sp.get("tab")) ?? "waiting") as NoticeTab;
  usePageName(t(PAYMENT_NOTICE_MSG.list));
  return (
    <ListHead title={t(PAYMENT_NOTICE_MSG.list)} extra={<PurchaseKindSwitch value="notices" />}>
      <NoticeTable
        create={<CreateLink />}
        tab={tab}
        onTab={(k) =>
          setSp((p) => {
            const n = new URLSearchParams(p);
            if (k === "waiting") n.delete("tab");
            else n.set("tab", k);
            return n;
          })
        }
      />
    </ListHead>
  );
}

/** {project}の画面のタブ「仕入」の下の段（その{project}の{paymentNotice}と「{paymentNotice}作成」） */
export function GroupNotices({ groupId }: { groupId: string }) {
  const [tab, setTab] = useState<NoticeTab>("all");
  return (
    <div data-testid="group-notices" style={{ marginTop: 24 }}>
      <h2 className="sec-h" style={{ fontSize: "var(--fs-17)", margin: "0 0 8px" }}>{t(PAYMENT_NOTICE_MSG.list)}</h2>
      <NoticeTable tab={tab} onTab={setTab} group={groupId} side={<CreateLink group={groupId} />} />
    </div>
  );
}

function CreateLink({ group }: { group?: string }) {
  const pcOnly = usePcOnly();
  if (pcOnly.hidden("/payment-notices/new")) return null;
  return (
    <Link className={group ? "btn" : "btn primary"} to={`/payment-notices/new${group ? `?group=${group}` : ""}`} data-testid="notice-new">
      {group ? null : <Icon k="plus" />}
      {t(PAYMENT_NOTICE_MSG.listCreate)}
    </Link>
  );
}

/** 一覧（create を渡すと一覧の頭の形＝表のカード・#241／{project}の画面のタブ＝side を件数の行の右に） */
function NoticeTable({ tab, onTab, group, side, create }: { tab: NoticeTab; onTab: (k: NoticeTab) => void; group?: string; side?: ReactNode; create?: ReactNode }) {
  const L = useLabels();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [qApplied, setQApplied] = useState("");
  useEffect(() => {
    const h = window.setTimeout(() => setQApplied(q.trim()), 300);
    return () => window.clearTimeout(h);
  }, [q]);
  const list = useQuery({
    queryKey: ["payment-notices", tab, qApplied, group ?? ""],
    queryFn: () => api<PaymentNoticeList>("GET", `/payment-notices?tab=${tab}${qApplied ? `&q=${encodeURIComponent(qApplied)}` : ""}${group ? `&group=${group}` : ""}`),
    placeholderData: keepPreviousData,
  });
  const items: PaymentNoticeSummary[] = list.data?.items ?? [];
  // 下書きは作成画面、出したものは紙の画面（送付・確認の記録）
  const open = (x: PaymentNoticeSummary) => nav(x.status === "draft" ? `/payment-notices/${x.id}` : `/payment-notices/${x.id}/paper`);
  const tid = group ? "gpn" : "pnl";
  const search = <FilterInput value={q} onChange={setQ} placeholder={t(PAYMENT_NOTICE_MSG.searchLabel)} testId="notice-search" />;
  const card = create !== undefined;
  const sum = (
    <div className="pul-sum" data-testid="notice-sum">
      <span>
        <b className="num">{list.data ? items.length : "…"}</b> 件
      </span>
      <span>
        合計 <b className="num">{list.data ? yen(list.data.total) : "…"}</b> 円
      </span>
      <span className="sp" />
      {side}
    </div>
  );
  return (
    <>
      {list.isError ? <ErrorText>{messageOf(list.error)}</ErrorText> : null}
      <div className={card ? "lcard" : undefined}>
      <ListTabs
        id={tid}
        label={t(PAYMENT_NOTICE_MSG.listTabs)}
        tabs={TABS.map((k) => ({ key: k, label: NOTICE_TAB_LABEL[k], count: list.data?.counts[k] ?? "…" }))}
        value={tab}
        onChange={(k) => onTab(k as NoticeTab)}
        filter={search}
        create={create}
      />
      <div className="tbl-wrap ltab-panel" {...listTabPanel(tid, tab)}>
        {card ? null : sum}
        <table className="list pnl-list" data-testid="notice-list">
          <thead>
            <tr>
              <th>{PAYMENT_NOTICE_MSG.colIssueDate}</th>
              <th>{PAYMENT_NOTICE_MSG.colNo}</th>
              <th>{L.vendor}</th>
              {group ? null : <th>{L.project}</th>}
              <th>{PAYMENT_NOTICE_MSG.colSubject}</th>
              <th className="amt">{PAYMENT_NOTICE_MSG.colAmount}</th>
              <th>{PAYMENT_NOTICE_MSG.colState}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((x) => (
              <tr key={x.id} className="r" tabIndex={0} onClick={() => open(x)} onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && open(x)} data-testid="notice-row">
                <td className="num">{mdSlash(x.issueDate)}</td>
                <td className="num">{x.documentNo ?? <span className="muted">—</span>}</td>
                <td>
                  <b>{x.vendorName}</b>
                </td>
                {group ? null : <td>{x.group?.name ?? <span className="muted">—</span>}</td>}
                <td>{x.subject || <span className="muted">—</span>}</td>
                <td className="num amt">{yen(x.total)}</td>
                <td className="statecell">
                  <NoticeStateChip state={x.state} waitingDays={x.waitingDays} confirmedAt={x.confirmedAt} documentNo={x.documentNo} deadline={x.deadline} />
                </td>
              </tr>
            ))}
            {list.data && !items.length ? (
              <tr className="emptyrows">
                <td colSpan={group ? 6 : 7}>{t(PAYMENT_NOTICE_MSG.empty)}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
        {/* #241：一覧の頭（表のカード）のときは、タブの段と表の見出しを接するため件数と合計の行は表の下 */}
        {card ? sum : null}
      </div>
      </div>
    </>
  );
}
